import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Stack, Inline, Heading, Link, Box, Text, IconPersonVerified, Rating, TextLink } from "braid-design-system";
import { useState } from "react";
import { useJobDetailsViewContext, useAnalyticsHandlers, VerifiedHirerInfo } from "../CompanyProfile/CompanyProfile.mjs";
import { useJobUrl, jobUrlOriginValues } from "../CompetitiveJobs/JobCardLite/JobCardLite.mjs";
import { translations } from "../../../components/JobDetailsViewHeader/.vocab/index.mjs";
import { hoverLink } from "./JobDetailsViewHeader.css.mjs";
const JobTitle = () => {
  const {
    hideJobLink,
    job,
    jobUrlOpts,
    renderRelativePaths,
    onAnalyticsEvent
  } = useJobDetailsViewContext();
  const url = useJobUrl({
    id: job.id,
    jobUrlOpts,
    renderRelativePaths,
    overrideValues: {
      origin: jobUrlOriginValues.jobTitle
    },
    isExpired: job.isExpired
  });
  const jobUrl = job.url || url;
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsx(Heading, { level: "3", component: "h1", data: { automation: "job-detail-title" }, children: hideJobLink ? job.title : /* @__PURE__ */ jsx(
    Link,
    {
      href: jobUrl,
      onClick: analytics.onJobTitleClick,
      className: hoverLink,
      target: "_blank",
      children: job.title
    }
  ) });
};
const CompanyName = () => {
  const { job, onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsx(Text, { data: { automation: "advertiser-name" }, children: job.company.profileUrl ? /* @__PURE__ */ jsx(
    Link,
    {
      href: job.company.profileUrl,
      className: hoverLink,
      onClick: analytics.onCompanyProfileClick,
      children: job.company.name
    }
  ) : job.company.name });
};
const VerifiedHirerName = ({
  trackVerifiedHirerBadgeClick,
  trackMoreAboutThisCompanyClick
}) => {
  const [showVerifiedHirerInfo, setShowVerifiedHirerInfo] = useState(false);
  const {
    job: {
      company: { name, isVerified }
    }
  } = useJobDetailsViewContext();
  return isVerified ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "button",
        onClick: () => {
          trackVerifiedHirerBadgeClick("Job Details");
          setShowVerifiedHirerInfo(true);
        },
        cursor: "pointer",
        className: hoverLink,
        children: /* @__PURE__ */ jsxs(Text, { align: "left", data: { automation: "advertiser-name" }, children: [
          name,
          " ",
          /* @__PURE__ */ jsx(IconPersonVerified, {})
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      VerifiedHirerInfo,
      {
        open: showVerifiedHirerInfo,
        closeFn: () => setShowVerifiedHirerInfo(false),
        trackMoreAboutThisCompanyClick
      }
    )
  ] }) : /* @__PURE__ */ jsx(Text, { data: { automation: "advertiser-name" }, children: name });
};
const CompanyLinks = () => {
  const {
    job: { company: { reviews, jobsSearchUrl } = {} },
    onAnalyticsEvent,
    featureFlags
  } = useJobDetailsViewContext();
  const shouldOpenLinkInNewTab = featureFlags == null ? void 0 : featureFlags.shouldOpenLinkInNewTab;
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { t } = useTranslations(translations);
  if (!reviews && !jobsSearchUrl) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "bottom", children: [
    reviews && reviews.count > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Rating,
        {
          size: "small",
          rating: reviews.rating,
          variant: "minimal",
          data: { automation: "company-review" }
        }
      ),
      (reviews == null ? void 0 : reviews.reviewsUrl) && /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
        TextLink,
        {
          href: reviews.reviewsUrl,
          rel: "nofollow",
          target: shouldOpenLinkInNewTab ? "_blank" : void 0,
          weight: "weak",
          "data-automation": "job-header-company-review-link",
          onClick: analytics.onCompanyReviewsClick,
          children: t("{JobCompanyReviewsCount} reviews", {
            JobCompanyReviewsCount: reviews.count.toString()
          })
        }
      ) })
    ] }) : null,
    jobsSearchUrl ? /* @__PURE__ */ jsxs(Fragment, { children: [
      (reviews == null ? void 0 : reviews.count) ? /* @__PURE__ */ jsx(Text, { size: "small", children: "·" }) : null,
      /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
        TextLink,
        {
          href: jobsSearchUrl,
          rel: "nofollow",
          onClick: analytics.onCompanyJobSearchClick,
          "data-automation": "job-details-header-more-jobs",
          weight: "weak",
          target: shouldOpenLinkInNewTab ? "_blank" : void 0,
          children: t("View all jobs")
        }
      ) })
    ] }) : null
  ] });
};
const JobDetailsViewHeader = ({
  trackVerifiedHirerBadgeClick,
  trackMoreAboutThisCompanyClick
}) => {
  const { featureFlags } = useJobDetailsViewContext();
  return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(JobTitle, {}),
    /* @__PURE__ */ jsxs(Inline, { alignY: "center", space: ["medium", "small"], children: [
      (featureFlags == null ? void 0 : featureFlags.enableVerifiedHirerBadge) ? /* @__PURE__ */ jsx(
        VerifiedHirerName,
        {
          trackVerifiedHirerBadgeClick,
          trackMoreAboutThisCompanyClick
        }
      ) : /* @__PURE__ */ jsx(CompanyName, {}),
      /* @__PURE__ */ jsx(CompanyLinks, {})
    ] })
  ] });
};
export {
  JobDetailsViewHeader
};
